import { Component, OnInit } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { Location as LocationV2 } from 'src/app/models/location';
import { EnvironmentService } from 'src/app/services/environment.service';
import { FacilityV2Service } from 'src/app/services/facilityV2.service';
import { FacilityConfig } from 'src/environments/environment.interface';

declare const google: any; // Declare the global Google Maps object

@Component({
  selector: 'app-location',
  templateUrl: './location.component.html',
  styleUrls: ['./location.component.scss']
})
export class LocationComponent implements OnInit {

  locationId: number = 1; // This should be set to the actual location ID you want to display
  location: LocationV2 = {
      name: '', description: '', address_line1: '', address_line2: '', city: '', state: '', zip_code: '', country: '', latitude: '', longitude: '', time_zone: '', id: 0,
      phone: '',
      email: '',
      website: '',
      tax_rate: 0,
      credit_card_surcharge: 0,
      location_hours: []
    };
  sanitizedMapUrl: any;
  directionUrl: string;
  settings: FacilityConfig;

  constructor(
      private sanitizer: DomSanitizer
    , private facilityV2Service: FacilityV2Service
    , private _environmentService: EnvironmentService
  ) { }

  async ngOnInit() {
    this.settings = this._environmentService.getFacilityConfig();
    this.location = await this.facilityV2Service.getLocationById(this.locationId);
    this.initializeMap();
    if (this.location.latitude && this.location.longitude) {
      const formattedDescription = encodeURIComponent(this.location.description);
      const mapUrl = `https://www.google.com/maps/embed/v1/place?key=${this.settings.googleMap.apiKey}&q=${formattedDescription}&center=${this.location.latitude},${this.location.longitude}&zoom=17`;
      this.sanitizedMapUrl = this.sanitizer.bypassSecurityTrustResourceUrl(mapUrl);

      this.directionUrl = `https://www.google.com/maps/dir/?api=1&destination=${this.location.latitude},${this.location.longitude}&travelmode=driving`;
    }
  }

  initializeMap(): void {
    if (this.location.latitude != '' && this.location.longitude != '') {
      const mapOptions = {
        center: { lat: parseFloat(this.location.latitude), lng: parseFloat(this.location.longitude) },
        zoom: 17,
      };

      const mapElement = document.getElementById('map');
      if (mapElement) {
        const map = new google.maps.Map(mapElement as HTMLElement, mapOptions);

        new google.maps.Marker({
          position: { lat: parseFloat(this.location.latitude), lng: parseFloat(this.location.longitude) },
          map: map,
          title: this.location.description
        });
      } else {
        console.error('Map element not found');
      }
    }
  }

  public currentYear(): string {
    return new Date().getFullYear().toString();
  }

  getDayName(weekday: number): string {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return days[weekday] || '';
  }

  formatTime(time: string): string {
    const [hour, minute] = time.split(':');
    const hourInt = parseInt(hour, 10);
    const isPM = hourInt >= 12;
    const formattedHour = hourInt % 12 || 12; // Convert 0 or 12-hour to 12-hour format
    const period = isPM ? 'PM' : 'AM';
    return `${formattedHour}:${minute.substring(0, 2)}${period}`;
  }

  groupLocationHours() {
    // Assume this.locationHours is the input array
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    // Map weekday to array of time ranges
    const dayTimes: { [key: number]: string[] } = {};

    // Helper to format time
    function formatTime(t: string) {
      // "07:00:00.000000" -> "7:00 AM"
      const [h, m] = t.split(':');
      let hour = parseInt(h, 10);
      const min = m;
      const ampm = hour >= 12 ? 'PM' : 'AM';
      hour = hour % 12 || 12;
      return `${hour}:${min} ${ampm}`;
    }

    // Group all open times by weekday
    for (const entry of this.location.location_hours) {
      if (entry.is_open) {
        const open = formatTime(entry.open_time);
        const close = formatTime(entry.close_time);
        const range = `${open} - ${close}`;
        if (!dayTimes[entry.weekday]) dayTimes[entry.weekday] = [];
        dayTimes[entry.weekday].push(range);
      }
    }

    // Build a list of objects: { days: [idx, ...], times: [...] }
    const result: { days: number[], times: string[] }[] = [];
    let prevTimes: string[] | null = null;
    let group: { days: number[], times: string[] } | null = null;

    for (let i = 0; i < 7; i++) {
      const times = dayTimes[i] || [];
      const timesKey = JSON.stringify(times);
      if (prevTimes && timesKey === JSON.stringify(prevTimes)) {
        if (group) {
          group.days.push(i);
        }
      } else {
        if (group) result.push(group);
        group = { days: [i], times };
        prevTimes = times;
      }
    }
    if (group) result.push(group);

    // Format for UI
    return result.map(g => {
      let range = '';
      if (g.days.length === 1) {
        range = days[g.days[0]];
      } else {
        range = `${days[g.days[0]]} - ${days[g.days[g.days.length - 1]]}`;
      }
      return {
        range,
        times: g.times
      };
    });
  }

}
